import type {
  FooterAdditionalInformationSectionConfig,
  FooterConfig,
  FooterContactSectionConfig,
  FooterCopyrightAndSocialsSectionConfig,
  FooterNavigationLinksSectionConfig,
  FooterUnderageSectionConfig,
} from '../../footer.types.ts';
import { ROUTES } from './routes/routes.ts';

const contactSection: FooterContactSectionConfig = {
  items: [
    {
      type: 'link',
      href: ROUTES.HELP_CENTRE.BASE_URL,
      iconKey: 'Help',
      labelKey: 'help_button',
      trackName: 'Help',
    },
    {
      type: 'chat',
      labelKey: 'live_chat_button',
      trackName: 'Chat',
    },
    {
      type: 'link',
      href: ROUTES.FOOTER.CONTACT.PHONE,
      iconKey: 'Phone',
      labelKey: 'phone_number',
      secondaryLabelKey: 'phone_hours',
      trackName: 'Phone',
    },
    {
      type: 'link',
      href: ROUTES.FOOTER.CONTACT.EMAIL,
      iconKey: 'Email',
      labelKey: 'email_button',
      secondaryLabelKey: 'email_address',
      trackName: 'Email',
    },
  ],
};

const navigationLinks: FooterNavigationLinksSectionConfig = {
  groups: [
    {
      titleKey: 'explore',
      trackName: 'Explore',
      links: [
        {
          href: ROUTES.FOOTER.NAVIGATION.EXPLORE.LIVE_DRAW,
          labelKey: 'live_draw',
          trackName: 'Live Draw',
        },
        {
          href: ROUTES.FOOTER.NAVIGATION.EXPLORE.CLASSIC,
          labelKey: 'kenogo_classic',
          trackName: 'KenoGO Classic',
        },
        {
          href: ROUTES.FOOTER.NAVIGATION.EXPLORE.JACKPOT,
          labelKey: 'kenogo_jackpot',
          trackName: 'KenoGO Jackpot',
        },
        {
          href: ROUTES.FOOTER.NAVIGATION.EXPLORE.HEAD_OR_TAILS,
          labelKey: 'kenogo_heads_or_tails',
          trackName: 'KenoGO Heads or Tails',
        },
        {
          href: ROUTES.FOOTER.NAVIGATION.EXPLORE.THE_DAILY_MILLION,
          labelKey: 'the_daily_million',
          trackName: 'The Daily Million',
        },
        {
          href: ROUTES.FOOTER.NAVIGATION.EXPLORE.LOTTERY_RESULTS,
          labelKey: 'lottery_results',
          trackName: 'Lottery Results',
        },
      ],
    },
    {
      titleKey: 'learn',
      trackName: 'Learn',
      links: [
        {
          href: ROUTES.FOOTER.NAVIGATION.LEARN.ABOUT_KENOGO,
          labelKey: 'about_kenogo',
          trackName: 'About KenoGO',
        },
        {
          href: ROUTES.FOOTER.NAVIGATION.LEARN.COMMUNITY_PARTNERSHIPS,
          labelKey: 'community_partnerships',
          trackName: 'Community Partnerships',
        },
        {
          href: ROUTES.FOOTER.NAVIGATION.LEARN.KENOGO_WINNERS,
          labelKey: 'kenogo_winners',
          trackName: 'KenoGO Winners',
        },
        {
          href: ROUTES.FOOTER.NAVIGATION.LEARN.HOW_TO_PLAY,
          labelKey: 'how_to_play',
          trackName: 'How to Play',
        },
        {
          href: ROUTES.FOOTER.NAVIGATION.LEARN.FAQ,
          labelKey: 'faq',
          trackName: 'FAQ',
        },
        {
          href: ROUTES.FOOTER.NAVIGATION.LEARN.APPS,
          labelKey: 'kenogo_apps',
          trackName: 'KenoGO Apps',
        },
      ],
    },
    {
      titleKey: 'legal_information',
      trackName: 'Legal Information',
      links: [
        {
          href: ROUTES.FOOTER.NAVIGATION.LEGAL.TERMS,
          labelKey: 'terms_and_conditions',
          trackName: 'Terms and Conditions',
        },
        {
          href: ROUTES.FOOTER.NAVIGATION.LEGAL.GAMBLE_SAFELY,
          labelKey: 'responsible_gambling',
          trackName: 'Responsible Gambling',
        },
        {
          href: ROUTES.FOOTER.NAVIGATION.LEGAL.PRIVACY,
          labelKey: 'privacy_policy',
          trackName: 'Privacy Policy',
        },
        {
          href: ROUTES.FOOTER.NAVIGATION.LEGAL.MARKETING_MESSAGES,
          labelKey: 'marketing_messages',
          trackName: 'Marketing Messages',
        },
        {
          href: ROUTES.FOOTER.NAVIGATION.LEGAL.KENOGO_RULES,
          labelKey: 'kenogo_rules',
          trackName: 'KenoGO Rules',
        },
        {
          href: ROUTES.FOOTER.NAVIGATION.LEGAL.LOTTERY_RULES,
          labelKey: 'lottery_rules',
          trackName: 'Lottery Rules',
        },
      ],
    },
  ],
};

const additionalInformation: FooterAdditionalInformationSectionConfig = {
  groups: [
    {
      titleKey: 'download_the_app',
      trackName: 'App Stores',
      items: [
        {
          imageKey: 'APP_STORE',
          alt: 'App Store',
          href: ROUTES.FOOTER.DOWNLOAD_THE_APP.APP_STORE,
          trackName: 'Apple',
          width: 140,
          height: 40,
          className: 'w-36 h-20 mr-4',
        },
        {
          imageKey: 'GOOGLE_PLAY',
          alt: 'Google Play',
          href: ROUTES.FOOTER.DOWNLOAD_THE_APP.GOOGLE_PLAY,
          trackName: 'Android',
          width: 140,
          height: 40,
          className: 'w-36 h-20',
        },
      ],
    },
    {
      titleKey: 'supported_charities',
      trackName: 'Charities',
      items: [
        {
          imageKey: 'RCHF',
          alt: "Royal Children's Hospital Foundation",
          href: ROUTES.NAVIGATION.PARTNERSHIPS,
          trackName: 'RCH Foundation',
          width: 80,
          height: 80,
          className: 'w-20 h-20',
        },
        {
          imageKey: 'SCHF',
          alt: "Sydney Children's Hospital Foundation",
          href: ROUTES.NAVIGATION.PARTNERSHIPS,
          trackName: 'SCH Foundation',
          width: 80,
          height: 80,
          className: 'w-20 h-20',
        },
      ],
    },
    {
      titleKey: 'responsible_gambling',
      trackName: 'Responsible Gambling',
      link: {
        href: ROUTES.FOOTER.RESPONSIBLE_GAMING.NT_INTERNET_GAMING_CODE,
        labelKey: 'nt_internet_gaming_code_of_practice',
        trackName: 'Gaming Code',
      },
      items: [
        {
          imageKey: 'GAMBLERS_HELP',
          alt: "Gambler's Help",
          href: ROUTES.FOOTER.RESPONSIBLE_GAMING.GAMBLERS_HELP,
          trackName: 'Gamblers Help',
          width: 80,
          height: 80,
          className: 'w-20 h-20',
        },
        {
          imageKey: 'RESET_APP',
          alt: 'Reset App',
          href: ROUTES.FOOTER.RESPONSIBLE_GAMING.RESET_APP,
          trackName: 'Reset',
          width: 80,
          height: 80,
          className: 'w-20 h-20',
        },
      ],
    },
  ],
};

const underageSection: FooterUnderageSectionConfig = {
  paragraphs: [
    {
      translationKey: 'legal_text',
    },
    {
      translationKey: 'responsible_gaming',
    },
    {
      translationKey: 'kenogo_logo',
    },
  ],
  imageSection: {
    leadImageTextKey: 'underage_gambling',
    leadImage: {
      imageKey: 'UNDERAGE_GAMBLING',
      alt: 'Underage gambling',
      width: 240,
      height: 160,
    },
  },
};

const copyrightAndSocials: FooterCopyrightAndSocialsSectionConfig = {
  socialLinks: [
    {
      href: ROUTES.FOOTER.SOCIALS.INSTAGRAM,
      label: 'Instagram',
      iconKey: 'Instagram',
      trackName: 'Instagram',
    },
    {
      href: ROUTES.FOOTER.SOCIALS.FACEBOOK,
      label: 'Facebook',
      iconKey: 'Facebook',
      trackName: 'Facebook',
    },
  ],
};

const footerConfig: FooterConfig = { contactSection, additionalInformation, copyrightAndSocials, navigationLinks, underageSection };

export { footerConfig };
